/**
 * Business type groups + niches for Create Business and discover filters.
 * Stored category value: `{typeId}:{nicheId}` (e.g. `coaching-courses:trading`).
 * Labels: `businessTypes.*` and `businessNiches.*` in i18n.
 */

export const SHARED_NICHES = Object.freeze([
  "trading",
  "sports-betting",
  "reselling",
  "fitness",
  "amazon-fba",
  "real-estate",
  "kindle-book-publishing",
  "dating",
  "agencies",
  "health-wellness",
  "social-media",
  "sales",
  "business",
  "ecommerce",
  "video-games",
  "home-services",
  "ai",
  "public-speaking",
  "personal-finance",
  "careers",
  "travel",
  "clipping",
  "spirituality",
  "vas",
  "personal-development",
  "software",
  "other",
]);

export const BUSINESS_TYPE_GROUPS = Object.freeze([
  {
    id: "coaching-courses",
    niches: SHARED_NICHES,
  },
  {
    id: "agency-services",
    niches: [
      "marketing-agency",
      "sales-agency",
      "ai-agency",
      "design-agency",
      "coaching-agency",
      "development-agency",
      "recruiting-agency",
      "customer-support-agency",
      "clipping-agency",
      "other",
    ],
  },
  {
    id: "paid-groups",
    niches: SHARED_NICHES,
  },
  {
    id: "software-saas",
    niches: SHARED_NICHES,
  },
  {
    id: "events",
    niches: [
      "masterminds",
      "webinars",
      "bootcamps",
      "conventions",
      "concerts",
      "meetups",
      "parties",
      "other",
    ],
  },
  {
    id: "newsletters",
    niches: SHARED_NICHES,
  },
  {
    id: "physical-products",
    niches: [
      "clothing",
      "beauty-personal-care",
      "fitness-gear",
      "accessories",
      "home-goods",
      "electronics-gadgets",
      "food-beverages",
      "other",
    ],
  },
  {
    id: "brick-and-mortar",
    niches: [
      "gym",
      "restaurant",
      "retail-store",
      "coffee-shop",
      "salon-spa",
      "medical-dental-office",
      "hotel-lodging",
      "auto-repair-shop",
      "other",
    ],
  },
]);

/** @deprecated flat list — use BUSINESS_TYPE_GROUPS */
export const BUSINESS_CATEGORIES = Object.freeze(
  BUSINESS_TYPE_GROUPS.flatMap((group) =>
    group.niches.map((niche) => ({
      id: encodeCategory(group.id, niche),
      groupId: group.id,
      nicheId: niche,
    }))
  )
);

export function encodeCategory(typeId, nicheId) {
  if (!typeId || !nicheId) return "";
  return `${typeId}:${nicheId}`;
}

export function parseCategory(value) {
  if (!value || typeof value !== "string") return { typeId: "", nicheId: "" };
  const idx = value.indexOf(":");
  if (idx === -1) {
    return { typeId: "", nicheId: value.trim() };
  }
  return {
    typeId: value.slice(0, idx),
    nicheId: value.slice(idx + 1),
  };
}

export function getBusinessTypeLabel(t, typeId) {
  if (!typeId) return "";
  return t(`businessTypes.${typeId}`) || typeId;
}

export function getBusinessNicheLabel(t, nicheId) {
  if (!nicheId) return "";
  return (
    t(`businessNiches.${nicheId}`) ||
    t(`businessCategories.${nicheId}`) ||
    nicheId.replace(/-/g, " ")
  );
}

/** One line for a card: "type · niche" runs too long there, and the niche says more. */
export function getBusinessCategoryShortLabel(t, categoryValue) {
  const { typeId, nicheId } = parseCategory(categoryValue);
  if (nicheId && nicheId !== "other") return getBusinessNicheLabel(t, nicheId);
  return typeId ? getBusinessTypeLabel(t, typeId) : "";
}

export function getBusinessCategoryLabel(t, categoryValue) {
  const { typeId, nicheId } = parseCategory(categoryValue);
  if (typeId && nicheId) {
    const type = getBusinessTypeLabel(t, typeId);
    const niche = getBusinessNicheLabel(t, nicheId);
    return `${type} · ${niche}`;
  }
  if (nicheId) {
    return getBusinessNicheLabel(t, nicheId);
  }
  return categoryValue || "";
}

export function getNichesForType(typeId) {
  const group = BUSINESS_TYPE_GROUPS.find((g) => g.id === typeId);
  return group?.niches ? [...group.niches] : [];
}
